import React, { useState, useEffect } from 'react';
import { 
  UserPlus, Pencil, Trash2, Shield, User, Mail, 
  Lock, Eye, EyeOff, X, Loader2, AlertTriangle
} from 'lucide-react';
import { usersAPI, authAPI } from '../../services/adminApi';
import { toast } from 'sonner';

const AdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'editor'
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const currentUser = authAPI.getUser();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await usersAPI.getAll();
      setUsers(data);
    } catch (error) {
      toast.error('Kullanıcılar yüklenemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ email: '', name: '', password: '', role: 'editor' });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role
    });
    setShowModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingUser) {
        // Update user
        const updateData = {
          email: formData.email,
          name: formData.name,
          role: formData.role
        };
        await usersAPI.update(editingUser.id, updateData);
        toast.success('Kullanıcı güncellendi');
      } else {
        // Create new user
        await usersAPI.create(formData);
        toast.success('Kullanıcı oluşturuldu');
      }
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }
    setSubmitting(true);

    try {
      await usersAPI.resetPassword(selectedUser.id, newPassword);
      toast.success('Şifre sıfırlandı');
      setShowPasswordModal(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await usersAPI.delete(selectedUser.id);
      toast.success('Kullanıcı silindi');
      setShowDeleteModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
          style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}>
          <Shield size={12} />
          Admin
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
        style={{ background: 'rgba(0, 240, 255, 0.2)', color: '#00F0FF' }}>
        <User size={12} />
        Editor
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin" size={32} style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-user-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>
            Kullanıcı Yönetimi
          </h1>
          <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
            Admin ve editör kullanıcılarını yönetin
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all"
          style={{
            background: 'linear-gradient(135deg, #00F0FF 0%, #0066FF 100%)',
            color: '#0F172A'
          }}
          data-testid="create-user-btn"
        >
          <UserPlus size={18} />
          Yeni Kullanıcı
        </button>
      </div>

      {/* Users Table */}
      <div className="rounded-xl overflow-hidden" style={{
        background: 'rgba(30, 41, 59, 0.6)',
        border: '1px solid rgba(0, 240, 255, 0.2)'
      }}>
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(0, 240, 255, 0.2)' }}>
              <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#94A3B8' }}>İsim</th>
              <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#94A3B8' }}>E-posta</th>
              <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#94A3B8' }}>Rol</th>
              <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#94A3B8' }}>Oluşturulma</th>
              <th className="px-6 py-4 text-right text-sm font-medium" style={{ color: '#94A3B8' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr 
                key={user.id} 
                style={{ borderBottom: '1px solid rgba(0, 240, 255, 0.1)' }}
                data-testid={`user-row-${user.id}`}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(0, 240, 255, 0.2)' }}>
                      <User size={20} style={{ color: '#00F0FF' }} />
                    </div>
                    <span style={{ color: '#F8FAFC' }}>{user.name}</span>
                    {user.email === currentUser?.email && (
                      <span className="text-xs px-2 py-0.5 rounded" 
                        style={{ background: 'rgba(74, 222, 128, 0.2)', color: '#4ADE80' }}>
                        Siz
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4" style={{ color: '#94A3B8' }}>{user.email}</td>
                <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                <td className="px-6 py-4" style={{ color: '#94A3B8' }}>
                  {new Date(user.created_at).toLocaleDateString('tr-TR')}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => openEditModal(user)}
                      className="p-2 rounded-lg transition-colors hover:bg-white/10"
                      style={{ color: '#00F0FF' }}
                      title="Düzenle"
                      data-testid={`edit-user-${user.id}`}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => openPasswordModal(user)}
                      className="p-2 rounded-lg transition-colors hover:bg-white/10"
                      style={{ color: '#FBBF24' }}
                      title="Şifre Sıfırla"
                      data-testid={`reset-password-${user.id}`}
                    >
                      <Lock size={16} />
                    </button>
                    {user.email !== currentUser?.email && (
                      <button
                        onClick={() => openDeleteModal(user)}
                        className="p-2 rounded-lg transition-colors hover:bg-white/10"
                        style={{ color: '#EF4444' }}
                        title="Sil"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="text-center py-12" style={{ color: '#64748B' }}>
            Henüz kullanıcı yok
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-xl p-6" style={{
            background: '#1E293B',
            border: '1px solid rgba(0, 240, 255, 0.3)'
          }} data-testid="user-modal">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold" style={{ color: '#F8FAFC' }}>
                {editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ color: '#94A3B8' }}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                  İsim
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    color: '#F8FAFC'
                  }}
                  data-testid="user-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                  E-posta
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    color: '#F8FAFC'
                  }}
                  data-testid="user-email-input"
                />
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                    Şifre
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      className="w-full px-4 py-3 rounded-lg outline-none transition-all pr-12"
                      style={{
                        background: 'rgba(15, 23, 42, 0.8)',
                        border: '1px solid rgba(0, 240, 255, 0.3)',
                        color: '#F8FAFC'
                      }}
                      data-testid="user-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: '#94A3B8' }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                  Rol
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    color: '#F8FAFC'
                  }}
                  data-testid="user-role-select"
                >
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 rounded-lg font-medium transition-colors"
                  style={{
                    background: 'rgba(100, 116, 139, 0.2)',
                    color: '#94A3B8'
                  }}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #00F0FF 0%, #0066FF 100%)',
                    color: '#0F172A'
                  }}
                  data-testid="save-user-btn"
                >
                  {submitting && <Loader2 className="animate-spin" size={18} />}
                  {editingUser ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-xl p-6" style={{
            background: '#1E293B',
            border: '1px solid rgba(0, 240, 255, 0.3)'
          }} data-testid="password-modal">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold" style={{ color: '#F8FAFC' }}>
                Şifre Sıfırla
              </h2>
              <button onClick={() => setShowPasswordModal(false)} style={{ color: '#94A3B8' }}>
                <X size={24} />
              </button>
            </div>

            <p className="mb-4" style={{ color: '#94A3B8' }}>
              <strong style={{ color: '#F8FAFC' }}>{selectedUser.name}</strong> için yeni şifre belirleyin
            </p>

            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                  Yeni Şifre
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-4 py-3 rounded-lg outline-none transition-all pr-12"
                    style={{
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(0, 240, 255, 0.3)',
                      color: '#F8FAFC'
                    }}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: '#94A3B8' }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 px-4 py-3 rounded-lg font-medium transition-colors"
                  style={{
                    background: 'rgba(100, 116, 139, 0.2)',
                    color: '#94A3B8'
                  }}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)',
                    color: '#0F172A'
                  }}
                  data-testid="confirm-password-btn"
                >
                  {submitting && <Loader2 className="animate-spin" size={18} />}
                  Şifreyi Sıfırla
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-xl p-6" style={{
            background: '#1E293B',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }} data-testid="delete-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                style={{ background: 'rgba(239, 68, 68, 0.2)' }}>
                <AlertTriangle size={24} style={{ color: '#EF4444' }} />
              </div>
              <h2 className="text-xl font-bold" style={{ color: '#F8FAFC' }}>
                Kullanıcı Sil
              </h2>
            </div>

            <p className="mb-6" style={{ color: '#94A3B8' }}>
              <strong style={{ color: '#F8FAFC' }}>{selectedUser.name}</strong> ({selectedUser.email}) kullanıcısını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-3 rounded-lg font-medium transition-colors"
                style={{
                  background: 'rgba(100, 116, 139, 0.2)',
                  color: '#94A3B8'
                }}
              >
                İptal
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                className="flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  color: '#FFFFFF'
                }}
                data-testid="confirm-delete-btn"
              >
                {submitting && <Loader2 className="animate-spin" size={18} />}
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;
